/* eslint-disable no-plusplus */
import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAppDispatch, useAppSelector } from "../../../store";
import AssetDownloadModal from "../../../components/modals/AssetDownloadModal";
import AssetDetailModal from "../../../components/modals/AssetDetailModal";
import ManageFavoriteModal from "../../../components/modals/ManageFavoriteModal";
import useMultiSelection from "../../../hooks/useMultiSelection";
import { setBreadcrumbState } from "../../../components/breadcrumbs/breadcrumbSlice";
import { AssetInfo } from "../../../models/interfaces/common";
import { getAssetMetadataByAssetIds } from "../../../utils/catalog";
import { toggleDownloadIsOpen } from "../../downloadSlice";
import { setAssetIdList } from "../../assetsSlice";
import CatalogCards from "../../../components/CatalogCards";
import {
  initialState,
  setInPageNavState,
} from "../../../components/inPageNavSlice";
import BigLoader from "../../../components/loaders/BigLoader";

const NoResultContent = ({ t }: { t: TFunction<"common", undefined> }) => (
  <div className="nr-wrap">
    <div className="no-results">
      <h1>{t("We’re sorry. Nothing matches your search.")}</h1>
      <p>
        {t("Don’t give up! Check the spelling, or try less specific terms.")}
      </p>
    </div>
  </div>
);

export default function PosterSignage() {
  // Translation
  const { t } = useTranslation("common");

  // Redux
  const dispatch = useAppDispatch();

  // API Parameters
  const primaryRegion = useAppSelector(
    (state) => state.user?.primaryRegion
  ) as string;
  const pageNumber = useAppSelector((state) => state.sort.pageNumber);
  const assetIds = useAppSelector((state) => state.assets.assetIdList);
  const [restApiAssets, setRestApiAssets] = useState<AssetInfo[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastProcessedAssetIds, setLastProcessedAssetIds] = useState<string[]>(
    []
  );
  const [isPagination, setIsPagination] = useState<boolean>(false);
  const [lastFilterHash, setLastFilterHash] = useState<string>("");
  const source = axios.CancelToken.source();

  // Function to set the assetIds in the Redux store
  const setAssetIds = (ids: string[]) => dispatch(setAssetIdList(ids));

  // Function to set the loader value in the Redux store
  const [smallLoading, setSmallLoading] = useState<boolean>(false);

  // Sorting Direction
  const sortingOrder = useAppSelector((state) => state.sort.order);
  const sortingDirection = useAppSelector((state) => state.sort.direction);

  // Modal States
  const [showAssetDetail, setShowAssetDetail] = useState(false);
  const [showManageFavorite, setShowManageFavorite] = useState(false);
  const [isAddToFavoriteModalTrigger, setIsAddToFavoriteModalTrigger] =
    useState(false);
  const downloadModalState = useAppSelector((state) => state.download.isOpen);
  const toggleDownloadModal = () => dispatch(toggleDownloadIsOpen());
  const toggleAssetDetail = () => setShowAssetDetail(!showAssetDetail);
  const toggleManageFavoriteModal = () => {
    setIsAddToFavoriteModalTrigger(true);
    setShowManageFavorite(!showManageFavorite);
  };

  // Selected Assets
  const initialSelection: string[] = [];
  const { selectedList } = useMultiSelection(
    "getAssetListing",
    "image",
    initialSelection
  );

  // Pagination
  const [hasNext, setHasNext] = useState<boolean>(false);

  dispatch(setBreadcrumbState(true));

  async function getAssetInfo() {
    if (!primaryRegion || assetIds.length === 0) return;

    // Check if we've already processed these exact assetIds
    const assetIdsString = assetIds.join(",");
    const lastProcessedString = lastProcessedAssetIds.join(",");

    if (assetIdsString === lastProcessedString && assetIds.length > 0) {
      return;
    }

    // For pagination, check if we already have some of these assets
    if (isPagination && restApiAssets && restApiAssets.length > 0) {
      const currentAssetIds = restApiAssets.map((asset) => asset.id);
      const newAssetIds = assetIds.filter(
        (id) => !currentAssetIds.includes(id)
      );

      if (newAssetIds.length === 0) {
        setLastProcessedAssetIds([...assetIds]);
        return;
      }
    }

    // For new filters (not pagination), check if we have all assets
    if (!isPagination && restApiAssets && restApiAssets.length > 0) {
      const currentAssetIds = restApiAssets.map((asset) => asset.id);
      const hasAllAssets = assetIds.every((id) => currentAssetIds.includes(id));
      if (hasAllAssets && assetIds.length === restApiAssets.length) {
        setLastProcessedAssetIds([...assetIds]);
        return;
      }
    }
    setLastProcessedAssetIds([...assetIds]);

    await getAssetMetadataByAssetIds(
      assetIds,
      primaryRegion,
      restApiAssets,
      setRestApiAssets,
      setLoading,
      pageNumber,
      source,
      isPagination,
      setSmallLoading
    );
  }

  useEffect(() => {
    setRestApiAssets(null);
    setAssetIds([]);
    setLastProcessedAssetIds([]);
    setIsPagination(false);
    setLastFilterHash("");
  }, [primaryRegion]);

  // Detect if this is pagination or new filters by comparing assetIds
  useEffect(() => {
    if (assetIds.length > 0) {
      const currentFilterHash = assetIds.join(",");

      // If assetIds are different from last time, it's new filters (not pagination)
      if (lastFilterHash !== "" && lastFilterHash !== currentFilterHash) {
        setIsPagination(false);
      } else if (lastFilterHash === currentFilterHash && pageNumber > 1) {
        // Same filters but page > 1 means pagination
        setIsPagination(true);
      } else if (pageNumber === 1) {
        // First page with new filters
        setIsPagination(false);
      }

      setLastFilterHash(currentFilterHash);
    }
  }, [assetIds, pageNumber, lastFilterHash]);

  useEffect(() => {
    getAssetInfo();
    return () => {
      source.cancel("Component unmounted");
    };
  }, [assetIds, pageNumber, sortingDirection, sortingOrder, primaryRegion]);

  useEffect(() => {
    dispatch(setInPageNavState(initialState));
  }, []);

  if (!loading && (!assetIds || assetIds.length === 0)) {
    return <NoResultContent t={t} />;
  }

  if (loading) {
    return <BigLoader t={t} />;
  }

  return (
    <div className="catalog-page ">
      <div className="cp container-fluid">
        <div className="cards">
          <div className="row">
            <CatalogCards
              loading={smallLoading}
              hasNext={hasNext}
              setHasNext={setHasNext}
              restApiAssets={restApiAssets}
              toggleDownloadModal={toggleDownloadModal}
              toggleAssetDetail={toggleAssetDetail}
              toggleManageFavoriteModal={toggleManageFavoriteModal}
            />
            <AssetDownloadModal
              isOpen={downloadModalState}
              toggle={toggleDownloadModal}
              selectedList={selectedList}
              modalClass={
                selectedList.length > 1
                  ? "downloadMultipleModal"
                  : "downloadModal"
              }
              centered
              backdrop="static"
              keyboard
              isMultipleDownload={selectedList.length > 1}
              useMultiSelectionData={false}
            />
            <AssetDetailModal
              isOpen={showAssetDetail}
              toggle={toggleAssetDetail}
              modalClass="assetDetailsModal"
              centered
              backdrop="static"
              keyboard
              fullscreen
            />
            <ManageFavoriteModal
              isOpen={showManageFavorite}
              toggle={toggleManageFavoriteModal}
              modalClass="favoritesModal"
              centered
              backdrop="static"
              keyboard
              isAddToFavorite={isAddToFavoriteModalTrigger}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
